import { useMutation } from '@apollo/client';
import { useState, useEffect, memo, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { toast } from 'react-toastify';
import { SelectChangeEvent } from '@mui/material';

import theme from '@styles/theme';
import { Mutation, Visitors } from '@graphql/types';
import { dateFormat, autoHyphen, handleTextLength } from '@utils/common';
import {
    VISITOR_TYPE,
    PARKING_STATUS_OPTIONS,
    STATUS_TYPE,
} from '@constants/index';
import { UPDATE_VISITOR_PARKING_STATUS_V2 } from '@graphql/mutations';
import { useAuth } from '@context/authProvider';

import MuiSelect from '@components/styled/MuiSelect';

type ParkingListProps = {
    list?: Visitors;
    refetch?: () => void;
};

const ParkingList = ({ list, refetch }: ParkingListProps) => {
    const { userLogout } = useAuth();
    const [status, setStatus] = useState('');
    const {
        rv_id,
        rv_createdAt,
        rv_type,
        rv_team,
        rv_companyName,
        rv_name,
        rv_cellPhone,
        rv_carRegistStatus,
        rv_carLicenseNumber,
        rv_hour,
        rv_min,
        rv_managerName,
        rv_visitorCardStatus,
        rv_visitorCardSerial,
    } = list ?? {};

    useEffect(() => {
        setStatus(rv_carRegistStatus as string);
    }, [rv_carRegistStatus]);

    const [updateVisitorParkingStatus_v2] = useMutation<
        Pick<Mutation, 'updateVisitorParkingStatus_v2'>
    >(UPDATE_VISITOR_PARKING_STATUS_V2);

    const submitHandler = useCallback(
        async (statusType: string) => {
            await updateVisitorParkingStatus_v2({
                variables: {
                    rvId: rv_id,
                    rvCarRegistStatus: statusType,
                },
                onCompleted: (data) => {
                    if (
                        !data.updateVisitorParkingStatus_v2
                            ?.tokenExpired as boolean
                    )
                        userLogout();
                    refetch!();
                    toast.success('주차 등록 상태를 변경했습니다.');
                },
                onError: (err) => console.log(err),
            });
        },
        [rv_id, refetch, updateVisitorParkingStatus_v2, userLogout],
    );

    const changeSelectHandler = async (
        e:
            | React.ChangeEvent<HTMLSelectElement | HTMLInputElement>
            | SelectChangeEvent,
    ) => {
        const { value } = e.target;
        if (!window.confirm('상태를 변경하시겠습니까?')) return;
        await submitHandler(value);
    };

    if (!list)
        return (
            <TableTitle>
                <CellStatus>상태</CellStatus>
                <CellRequestDate>요청일시</CellRequestDate>
                <CellVisitPass>방문증</CellVisitPass>
                <CellVisitStatus>방문상태</CellVisitStatus>
                <CellType>구분</CellType>
                <CellCompany>업체명/소속</CellCompany>
                <CellName>이름</CellName>
                <CellPhoneNum>연락처</CellPhoneNum>
                <CellCarNum>차량번호</CellCarNum>
                <CellExpectedDeparture>출차예정시간</CellExpectedDeparture>
                <CellManager>담당자명</CellManager>
            </TableTitle>
        );

    return (
        <Container $disabled={status === 'complete' || status === 'cancel'}>
            <CellStatus>
                <MuiSelect
                    name="status"
                    value={status}
                    onChange={(e) => changeSelectHandler(e)}
                    options={PARKING_STATUS_OPTIONS}
                    border={false}
                    disabled={status === 'complete' || status === 'cancel'}
                />
            </CellStatus>
            <CellRequestDate>
                {rv_createdAt
                    ? (dateFormat(
                          rv_createdAt as string,
                          'yyyy-MM-dd hh:mm',
                      ) as string)
                    : '-'}
            </CellRequestDate>
            <CellVisitPass>
                {rv_visitorCardSerial ? rv_visitorCardSerial : '-'}
            </CellVisitPass>
            <CellVisitStatus>
                {STATUS_TYPE[rv_visitorCardStatus as string]}
            </CellVisitStatus>
            <CellType>{VISITOR_TYPE[rv_type as string]}</CellType>
            <CellCompany>
                {rv_companyName ? handleTextLength(rv_companyName, 10) : '-'}
            </CellCompany>
            <CellName>{rv_name ? rv_name : '-'}</CellName>
            <CellPhoneNum>
                {rv_cellPhone ? autoHyphen(rv_cellPhone as string) : '-'}
            </CellPhoneNum>
            <CellCarNum>
                {rv_carLicenseNumber ? rv_carLicenseNumber : '-'}
            </CellCarNum>
            <CellExpectedDeparture>
                {rv_hour ? `${rv_hour}:${rv_min ? rv_min : '00'}` : '-'}
            </CellExpectedDeparture>
            <CellManager>{`${rv_team ? handleTextLength(rv_team, 5) : ''} ${
                rv_managerName ? rv_managerName : '-'
            }`}</CellManager>
        </Container>
    );
};

export default memo(ParkingList);

const Container = styled.li<{ $disabled: boolean }>`
    display: flex;
    height: 50px;
    border-bottom: ${theme.colors.componentBorder} 1px solid;

    ${({ $disabled }) =>
        $disabled &&
        css`
            color: ${theme.colors.disabled};
        `};

    ${({ $disabled }) =>
        !$disabled &&
        css`
            &:hover {
                background-color: ${theme.colors.componentBG};
            }
        `};
`;

const TableTitle = styled.li`
    display: flex;
    height: 50px;
    border-bottom: ${theme.colors.blackColor} 1px solid;
`;

const TableCell = styled.span`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 10px;
    height: inherit;
    text-align: center;
    word-break: keep-all;
    line-height: 1.2;
    /* text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
    line-height: 1.5; */
`;

const CellStatus = styled(TableCell)`
    flex: 9%;
`;

const CellRequestDate = styled(TableCell)`
    flex: 11%;
`;

const CellVisitPass = styled(TableCell)`
    flex: 8%;
`;

const CellVisitStatus = styled(TableCell)`
    flex: 8%;
`;

const CellType = styled(TableCell)`
    flex: 8%;
`;

const CellCompany = styled(TableCell)`
    flex: 12%;
`;

const CellName = styled(TableCell)`
    flex: 7%;
`;

const CellPhoneNum = styled(TableCell)`
    flex: 9%;
`;

const CellCarNum = styled(TableCell)`
    flex: 9%;
`;

const CellExpectedDeparture = styled(TableCell)`
    flex: 9%;
`;

const CellManager = styled(TableCell)`
    flex: 10%;
`;
